import { inject, Injectable } from '@angular/core';
import { SessionStorageService } from '../../../../../core/services/session-storage/session-storage.service';
import { environment } from '../../../../../../environments/environment';
import axios, { AxiosError, AxiosRequestConfig } from 'axios';
import { ExportFileModel } from '../export-file-models/export-file.models';
import { ExportFileHistoryEntry } from '../export-file-interface/export-file.interaface';

/** MIME type of the workbook the export endpoint returns. */
const XLSX_MIME = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';

/** Filename used when the response carries no `Content-Disposition`. */
const FALLBACK_FILENAME = 'AdvisorMaintenanceData';

@Injectable({
  providedIn: 'root'
})
export class ExportFileService {

  private sessionStorageService = inject(SessionStorageService);

  constructor() { }

  async exportExcel(exportexcelModel: ExportFileModel) {

    const API_URL: string = environment.baseUrl + environment.apis.exportMaintanancedata;

    const REQUEST_CONFIG: AxiosRequestConfig = {
      method: 'POST',
      maxBodyLength: Infinity,
      url: API_URL,
      responseType: 'blob',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': this.sessionStorageService.getCurrentUserAuthToken()
      },
      data: exportexcelModel.toString(),
    };

    let httpResponse;
    try {
      httpResponse = await axios.request(REQUEST_CONFIG);
    } catch (error) {
      // A blob-typed request fails with a Blob body, not JSON — decode it before
      // it reaches the caller, or the message reads as "[object Blob]".
      throw await this.toBlobAxiosError(error);
    }

    // A JSON body on a 2xx is an error the endpoint chose not to signal with a
    // status code. Saving it would hand the user a workbook Excel refuses to
    // open, so it is surfaced as a failure instead.
    const body: Blob = httpResponse.data;
    if (body?.type?.includes('json')) {
      throw new AxiosError(await body.text(), String(httpResponse.status) as any);
    }

    const blob = new Blob([body], { type: XLSX_MIME });
    const objectUrl = URL.createObjectURL(blob);

    const a = document.createElement('a');
    a.href = objectUrl;
    a.download = this.filenameFrom(httpResponse.headers?.['content-disposition']);
    document.body.appendChild(a);
    a.click();
    a.remove();

    // Revoking in the same tick can cancel the download in some browsers — the
    // click only queues it. One turn of the event loop is enough.
    setTimeout(() => URL.revokeObjectURL(objectUrl));
  }

  async saveExcelFieldshistory(exportexcelModel: ExportFileModel) {
    const API_URL: string = environment.baseUrl + environment.apis.saveExportFieldshistory;

    const REQUEST_CONFIG: AxiosRequestConfig = {
      method: 'POST',
      maxBodyLength: Infinity,
      url: API_URL,
      headers: {
        'Content-Type': 'application/json',
        'Authorization': this.sessionStorageService.getCurrentUserAuthToken()
      },
      data: exportexcelModel.toString()
    };

    return await axios.request(REQUEST_CONFIG)
      .then((httpResponse) => {
        return JSON.stringify(httpResponse);
      })
      .catch((error) => {
        throw this.toAxiosError(error);
      });
  }

  /**
   * The column sets this user has exported before, newest first.
   *
   * ⚠️ Unconfirmed API shape — the endpoint may answer with a list of entries or
   * with a single latest record, so both are normalised to a list here and an
   * unrecognised payload yields an empty one. Cross-check against the Go repo.
   * Never throws: seeding the picker is a convenience, not a precondition.
   */
  async getExcelFieldshistory(): Promise<ExportFileHistoryEntry[]> {
    const API_URL: string = environment.baseUrl + environment.apis.getExportFieldshistory;

    const REQUEST_CONFIG: AxiosRequestConfig = {
      method: 'GET',
      maxBodyLength: Infinity,
      url: API_URL,
      headers: {
        'Content-Type': 'application/json',
        'Authorization': this.sessionStorageService.getCurrentUserAuthToken()
      }
    };

    try {
      const httpResponse = await axios.request(REQUEST_CONFIG);
      const payload = httpResponse.data?.responsedata ?? httpResponse.data;

      if (Array.isArray(payload)) {
        return payload as ExportFileHistoryEntry[];
      }
      if (payload && typeof payload === 'object') {
        return [payload as ExportFileHistoryEntry];
      }
      return [];
    } catch {
      return [];
    }
  }

  // ── Error / header helpers ───────────────────────────────────

  /**
   * Normalises an axios rejection into the shape the dashboard components read:
   * `code` carries the HTTP status so callers can branch on 400 vs 401. A
   * transport failure has no `response` at all, which the previous
   * `error.response.data` access turned into a TypeError.
   */
  private toAxiosError(error: any, decodedBody?: string): AxiosError {
    const status = error?.response?.status ?? error?.status;
    const message =
      decodedBody ??
      (typeof error?.response?.data === 'string' ? error.response.data : null) ??
      error?.response?.data?.message ??
      error?.message ??
      'Request failed';

    // `code` carries the numeric HTTP status, not axios's own string code — the
    // dashboard components branch on it with `HttpStatusCode`, so the type is
    // widened here to match the convention the rest of the services use.
    return new AxiosError(message, status as unknown as string);
  }

  /** As `toAxiosError`, first decoding a Blob error body to text. */
  private async toBlobAxiosError(error: any): Promise<AxiosError> {
    const body = error?.response?.data;
    if (body instanceof Blob) {
      try {
        return this.toAxiosError(error, await body.text());
      } catch {
        // Fall through — an unreadable body is no reason to lose the status.
      }
    }
    return this.toAxiosError(error);
  }

  /**
   * Filename from a `Content-Disposition` header, so the sheet keeps whatever the
   * server named it. Falls back to a date-stamped name — a fixed one collides in
   * the browser's download folder on every repeat export.
   */
  private filenameFrom(contentDisposition: unknown): string {
    if (typeof contentDisposition === 'string') {
      // `filename*=UTF-8''name.xlsx` takes precedence over plain `filename=`.
      const match =
        /filename\*=(?:UTF-8'')?"?([^";]+)"?/i.exec(contentDisposition) ??
        /filename="?([^";]+)"?/i.exec(contentDisposition);

      if (match?.[1]) {
        return decodeURIComponent(match[1].trim());
      }
    }

    const stamp = new Date().toISOString().slice(0, 10);
    return `${FALLBACK_FILENAME}_${stamp}.xlsx`;
  }
}
